import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import loanService from '../../services/loanService';
import { useToast } from '../../contexts/ToastContext';
import { useCurrency } from '../../contexts/CurrencyContext';

const labels = {
  pending: 'En attente', approved: 'Approuvé', active: 'Actif',
  completed: 'Clôturé', defaulted: 'Défaillant', rejected: 'Rejeté',
  cancelled: 'Annulé', restructured: 'Restructuré',
};

const LoanDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const { formatCurrency } = useCurrency();
  const [loan, setLoan] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const [details, events] = await Promise.all([
        loanService.getLoan(id), loanService.getLoanHistory(id),
      ]);
      setLoan(details); setHistory(events);
    } catch (error) { showError(error.response?.data?.message || 'Chargement impossible'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (callback, success) => {
    try { await callback(); showSuccess(success); await load(); }
    catch (error) { showError(error.response?.data?.message || "L'opération a échoué"); }
  };
  const askReason = (message) => window.prompt(message)?.trim();

  if (loading) return <div className="p-8 text-center">Chargement…</div>;
  if (!loan) return <div className="p-8">Prêt introuvable.</div>;

  return <div className="space-y-6">
    <div className="flex flex-wrap justify-between gap-3">
      <div><button className="text-primary-600" onClick={() => navigate('/loans')}>← Retour</button>
        <h1 className="text-2xl font-semibold">{loan.loan_number}</h1>
        <p>{loan.client ? `${loan.client.first_name} ${loan.client.last_name}` : ''} · {labels[loan.status] || loan.status}</p>
      </div>
      <button className="btn-secondary" onClick={() => loanService.exportSchedule(id)}>Exporter l’échéancier</button>
    </div>

    <div className="grid sm:grid-cols-4 gap-4">
      {[['Principal', loan.amount], ['Total', loan.total_amount], ['Encaissé', loan.amount_paid], ['Solde', loan.amount_due]].map(([name, value]) =>
        <div className="card p-4" key={name}><div className="text-sm text-gray-500">{name}</div><div className="text-xl font-semibold">{formatCurrency(value)}</div></div>)}
    </div>

    <div className="card p-5 space-y-3">
      <h2 className="font-semibold">Actions du workflow</h2>
      <div className="flex flex-wrap gap-2">
        {loan.status === 'pending' && <>
          <button className="btn-secondary" onClick={() => act(() => loanService.validateGuarantees(id, { status: 'validated', notes: 'Contrôle effectué' }), 'Garanties validées')}>Valider les garanties</button>
          <button className="btn-primary" onClick={() => act(() => loanService.approveLoan(id, {}), 'Prêt approuvé')}>Approuver</button>
          <button className="btn-danger" onClick={() => { const reason = askReason('Motif du rejet'); if (reason) act(() => loanService.rejectLoan(id, { reason }), 'Prêt rejeté'); }}>Rejeter</button>
        </>}
        {loan.status === 'approved' && <button className="btn-primary" onClick={() => act(() => loanService.disburseLoan(id, {}), 'Prêt décaissé')}>Décaisser</button>}
        {['pending', 'approved'].includes(loan.status) && <button className="btn-secondary" onClick={() => { const reason = askReason("Motif d'annulation"); if (reason) act(() => loanService.cancelLoan(id, { reason }), 'Prêt annulé'); }}>Annuler</button>}
        {loan.status === 'active' && <button className="btn-danger" onClick={() => { const reason = askReason('Motif du défaut'); if (reason) act(() => loanService.markDefault(id, { reason }), 'Défaut enregistré'); }}>Déclarer en défaut</button>}
        {['active', 'defaulted'].includes(loan.status) && <button className="btn-secondary" onClick={() => { const reason = askReason('Motif de restructuration'); if (reason) act(() => loanService.restructureLoan(id, { reason, term: loan.term }), 'Restructuration créée'); }}>Restructurer</button>}
        {['active', 'defaulted'].includes(loan.status) && Number(loan.amount_due) === 0 && <button className="btn-primary" onClick={() => act(() => loanService.closeLoan(id), 'Prêt clôturé')}>Clôturer</button>}
      </div>
    </div>

    <div className="card p-5 overflow-x-auto"><h2 className="font-semibold mb-3">Échéancier et encaissements</h2>
      <table className="min-w-full text-sm"><thead><tr><th>#</th><th>Date</th><th>Total</th><th>Payé</th><th>Statut</th><th>Reçu</th></tr></thead>
        <tbody>{(loan.repayments || []).map(r => <tr key={r.id} className="border-t"><td>{r.installment_number}</td><td>{r.due_date}</td><td>{formatCurrency(r.total_amount)}</td><td>{formatCurrency(r.amount_paid)}</td><td>{r.status}</td><td>{Number(r.amount_paid) > 0 && <button className="text-primary-600" onClick={async () => { const receipt = await loanService.getRepaymentReceipt(id, r.id); const win = window.open('', '_blank'); win.document.write(`<pre>${JSON.stringify(receipt, null, 2)}</pre>`); }}>Voir</button>}</td></tr>)}</tbody>
      </table>
    </div>

    <div className="card p-5"><h2 className="font-semibold mb-3">Historique des statuts</h2>
      <div className="space-y-2">{history.map(event => <div key={event.id} className="border-l-2 border-primary-500 pl-3"><b>{labels[event.to_status] || event.to_status}</b> — {event.reason || 'Transition'}<div className="text-xs text-gray-500">{new Date(event.created_at).toLocaleString()}</div></div>)}</div>
    </div>

    <div className="card p-5"><div className="flex justify-between mb-3"><h2 className="font-semibold">Pièces justificatives</h2>
      <button className="text-primary-600" onClick={() => {
        const document_type = window.prompt('Type de document (identité, garantie, contrat…)')?.trim();
        const name = document_type && window.prompt('Nom du document')?.trim();
        const file_url = name && window.prompt('URL sécurisée du fichier')?.trim();
        if (document_type && name && file_url) act(() => loanService.addLoanDocument(id, { document_type, name, file_url }), 'Document ajouté');
      }}>+ Ajouter</button></div>
      <div className="space-y-2">{(loan.documents || []).map(document => <div key={document.id} className="flex justify-between border-b py-2"><span>{document.document_type} — {document.name}</span><a className="text-primary-600" href={document.file_url} target="_blank" rel="noreferrer">Ouvrir</a></div>)}
      {!loan.documents?.length && <p className="text-sm text-gray-500">Aucune pièce enregistrée.</p>}</div>
    </div>
  </div>;
};

export default LoanDetail;
